import { PostApiMethod } from './apiService';
import { GetTenantId } from './sharedService';

interface QueryRequest {
    query: string;
    max_results?: number;
    database_name?: string;
    session_id?: string;
    user_id?: string;
    tenant_id?: string;
}

interface ChatMessage {
    question: string;
    rephrase_question: string;
    is_on_topic?: boolean;
    answer?: {
        content: string;
        tables?: any;
        list?: any;
        graph?: any;
    };
    execution_time?: number;
    created_at: Date;
    updated_at: Date;
}

class ChatBotService {
    private readonly baseUrl: string;
    
    constructor() {
        // Validate and set base URL
        const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
        if (!apiEndpoint || !this.isValidUrl(apiEndpoint)) {
            throw new Error('Invalid API endpoint configuration');
        }
        this.baseUrl = apiEndpoint;
    }
    
    private isValidUrl(url: string): boolean {
        try {
            const parsed = new URL(url);
            // Only allow https in production
            if (process.env.NODE_ENV === 'production' && parsed.protocol !== 'https:') {
                return false;
            }
            return true;
        } catch {
            return false;
        }
    }
    
    private getAuthHeaders(tenantId: string): HeadersInit {
        const token = localStorage.getItem('access_Token');
        if (!token) {
            throw new Error('Authentication token not found');
        }
        
        return {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'tenant': tenantId
        };
    }

    async sendStreamingQueryWithContext(
        query: string,
        sessionId: string,
        userId: string,
        tenantId: string,
        onChunk: (chunk: string, isComplete: boolean, metadata?: any) => void,
        onError: (error: string) => void,
        maxResults: number = 100,
        databaseName?: string
    ): Promise<void> {
        // Construct tenant-specific database name
        const tenantDatabaseName = tenantId ? `tn_${tenantId}` : databaseName;
        const requestBody: QueryRequest = {
            query,
            max_results: maxResults,
            database_name: tenantDatabaseName,
            session_id: sessionId,
            user_id: userId,
            tenant_id: tenantId
        };

        try {
            // Validate inputs
            if (!query || !tenantId || !userId) {
                throw new Error('Missing required parameters');
            }
            
            const response = await fetch(`${this.baseUrl}/admin/query/stream`, {
                method: 'POST',
                headers: this.getAuthHeaders(tenantId),
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const reader = response.body?.getReader();
            const decoder = new TextDecoder();

            if (!reader) {
                throw new Error('No reader available');
            }

            while (true) {
                const { done, value } = await reader.read();
                
                if (done) break;
                
                const chunk = decoder.decode(value);
                const lines = chunk.split('\n');
                
                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        try {
                            const data = JSON.parse(line.slice(6));
                            if (data.error) {
                                onError(data.chunk);
                                return;
                            }
                            onChunk(data.chunk, data.isComplete, data.metadata);
                            if (data.isComplete) return;
                        } catch (e) {
                            console.error('Error parsing SSE data:', e);
                        }
                    }
                }
            }
        } catch (error: any) {
            console.error('Streaming error:', error);
            onError(error.message || 'Failed to stream response');
        }
    }


    async getFullConversationContext(sessionId: string, userId: string, tenantId: string): Promise<ChatMessage[]> {
        try {
            if (!sessionId || !userId || !tenantId) {
                throw new Error('Missing required parameters');
            }
            
            const params = new URLSearchParams({
                session_id: sessionId,
                user_id: userId,
                tenant_id: tenantId
            });

            const response = await fetch(`${this.baseUrl}/admin/chat/context/full?${params}`, {
                method: 'GET',
                headers: this.getAuthHeaders(tenantId)
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Failed to fetch conversation context: ${response.status} - ${errorText}`);
            }

            const data = await response.json();
            if (!data || !data.data || !Array.isArray(data.data.context)) {
                throw new Error('Invalid response format');
            }
            
            return data.data.context;
        } catch (error: any) {
            console.error('[ERROR] Failed to get conversation context:', error.message);
            throw error;
        }
    }

    async getSessionStats(sessionId: string, tenantId?: string): Promise<any> {
        try {
            const params = new URLSearchParams({
                session_id: sessionId
            });
            
            if (tenantId) {
                params.append('tenant_id', tenantId);
            }

            const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/admin/chat/stats?${params}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('access_Token')}` || '',
                    'tenant': tenantId || GetTenantId()
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data.data;
        } catch (error: any) {
            console.error('Error getting session stats:', error);
            return null;
        }
    }

    // Utility method to generate session ID
    generateSessionId(): string {
        return `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    // Chat History Management Methods
    async getUserChatSessions(userId: string, tenantId: string): Promise<any[]> {
        try {
            const params = new URLSearchParams({
                user_id: userId,
                tenant_id: tenantId
            });

            const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/admin/chat/sessions?${params}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('access_Token')}` || '',
                    'tenant': tenantId
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data.data;
        } catch (error: any) {
            console.error('Error getting user chat sessions:', error);
            return [];
        }
    }

    async createNewChatSession(userId: string, tenantId: string, title: string = 'New Chat'): Promise<any> {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/admin/chat/sessions/new`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('access_Token')}` || '',
                    'tenant': tenantId
                },
                body: JSON.stringify({
                    user_id: userId,
                    tenant_id: tenantId,
                    title: title
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data.data;
        } catch (error: any) {
            console.error('Error creating new chat session:', error);
            throw new Error('Failed to create new chat session');
        }
    }

    async renameChatSession(sessionId: string, title: string, userId: string, tenantId: string): Promise<any> {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/admin/chat/sessions/rename`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('access_Token')}` || '',
                    'tenant': tenantId
                },
                body: JSON.stringify({
                    session_id: sessionId,
                    title: title,
                    user_id: userId,
                    tenant_id: tenantId
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data.data;
        } catch (error: any) {
            console.error('Error renaming chat session:', error);
            throw new Error('Failed to rename chat session');
        }
    }

    async deleteChatSession(sessionId: string, userId: string, tenantId: string): Promise<void> {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/admin/chat/sessions/delete`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('access_Token')}` || '',
                    'tenant': tenantId
                },
                body: JSON.stringify({
                    session_id: sessionId,
                    user_id: userId,
                    tenant_id: tenantId
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
        } catch (error: any) {
            console.error('Error deleting chat session:', error);
            throw new Error('Failed to delete chat session');
        }
    }

    async searchChatSessions(userId: string, tenantId: string, query: string): Promise<any[]> {
        try {
            const params = new URLSearchParams({
                user_id: userId,
                tenant_id: tenantId,
                query: query
            });

            const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/admin/chat/sessions/search?${params}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('access_Token')}` || '',
                    'tenant': tenantId
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data.data;
        } catch (error: any) {
            console.error('Error searching chat sessions:', error);
            return [];
        }
    }
}

export const chatBotService = new ChatBotService();
